import React from "react";
import { Shuffle, TrendingDown, Zap, Shield } from "lucide-react";

// AI Router — live cost-saving dashboard mockup
export const AIRouterMockup = () => {
  const providers = [
    { name: "Claude Haiku", share: 38, color: "#D46F4D", cost: "cheap" },
    { name: "GPT-4o-mini", share: 27, color: "#D9B99B", cost: "balanced" },
    { name: "Gemini Flash", share: 22, color: "#7CC79F", cost: "fast" },
    { name: "Local · Llama 3", share: 13, color: "#6B7280", cost: "free" },
  ];

  const events = [
    { type: "route", text: "→ Routed to Claude Haiku · saved $0.018", tone: "ok" },
    { type: "failover", text: "↻ Failover: GPT-4o → Gemini Flash · 0 ms", tone: "warn" },
    { type: "route", text: "→ Local model · saved $0.041", tone: "ok" },
  ];

  return (
    <div
      className="relative w-full h-full rounded-2xl border border-sand-soft overflow-hidden bg-[#0E0E10]"
    >
      <div
        aria-hidden
        className="absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, rgba(212,111,77,0.18), rgba(212,111,77,0) 70%)",
        }}
      />

      <div className="relative p-6 md:p-7 h-full flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shuffle size={14} className="text-terracotta" />
            <span className="font-display text-sm text-maif">AI Router</span>
            <span className="eyebrow text-[9px] text-maif-muted">· Smart routing</span>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[10px] text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            4 providers · live
          </span>
        </div>

        {/* Hero metrics */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg bg-terracotta/10 p-4">
            <div className="text-[9px] tracking-[0.2em] uppercase font-semibold text-terracotta">Saved this month</div>
            <div className="mt-1 font-display font-light text-3xl md:text-4xl text-terracotta tracking-tighter">
              $4,218
            </div>
            <div className="text-[10px] text-maif-muted mt-1">vs. single-provider baseline</div>
          </div>
          <div className="rounded-lg bg-white/[0.04] p-4">
            <div className="text-[9px] tracking-[0.2em] uppercase font-semibold text-sand">Cost reduction</div>
            <div className="mt-1 font-display font-light text-3xl md:text-4xl text-maif tracking-tighter">
              47%
            </div>
            <div className="text-[10px] text-emerald-300 mt-1 flex items-center gap-1">
              <TrendingDown size={10} /> trending down
            </div>
          </div>
        </div>

        {/* Provider routing breakdown */}
        <div className="mt-5">
          <div className="eyebrow text-[9px] mb-2">Routing distribution · last 24h</div>
          {/* Stacked bar */}
          <div className="h-2 w-full rounded-full overflow-hidden flex bg-white/[0.04]">
            {providers.map((p) => (
              <div
                key={p.name}
                style={{ width: `${p.share}%`, background: p.color }}
                className="h-full transition-all"
              />
            ))}
          </div>
          {/* Legend */}
          <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5">
            {providers.map((p) => (
              <div
                key={p.name}
                className="flex items-center justify-between text-[11px]"
              >
                <span className="flex items-center gap-1.5 text-maif-muted">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: p.color }}
                  />
                  {p.name}
                </span>
                <span className="text-maif">{p.share}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Live event feed */}
        <div className="mt-5 flex-1 rounded-lg bg-white/[0.03] p-3 space-y-1.5 overflow-hidden">
          <div className="flex items-center gap-2 pb-1 border-b border-sand-soft/50">
            <Zap size={11} className="text-terracotta" />
            <span className="text-[10px] text-maif-muted">Live event stream</span>
          </div>
          {events.map((e, i) => (
            <div
              key={i}
              className={`text-[11px] font-mono ${
                e.tone === "ok" ? "text-emerald-300/80" : "text-amber-300/80"
              }`}
            >
              {e.text}
            </div>
          ))}
        </div>

        {/* Footer chips */}
        <div className="mt-5 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 rounded-full border border-sand-soft bg-white/[0.02] px-2 py-0.5 text-[10px] text-maif-muted">
            OpenAI-compatible
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-sand-soft bg-white/[0.02] px-2 py-0.5 text-[10px] text-maif-muted">
            <Shield size={10} /> No prompts stored
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-sand-soft bg-white/[0.02] px-2 py-0.5 text-[10px] text-maif-muted">
            99.99% uptime
          </span>
        </div>
      </div>
    </div>
  );
};

export default AIRouterMockup;
